import { Component, inject, output } from '@angular/core';
import { ZiroFieldComponentConfig } from '../../../../../../forms/ziro-forms-models';
import { ZiroFieldAbstractDirective } from '../../../../../../forms/ziro-field-abstract.directive';
import { UniqueIdService } from '../../../../../../shared/services/unique-id.service';
import { ZiroSelectOption, ZiroSelectValue } from '../../../../../../forms/fields/select/ziro-select.component';
import { isArray, isEqual } from 'lodash';
import { NgModel } from '@angular/forms';
import { ZiroSelectService } from '../../../../../../forms/fields/select/ziro-select.service';

export interface ZiroRadioButtonSelect {
  value: string;
  label: string;
  labelParam?: any;
  options: ZiroSelectOption[];
  isMultiSelect: boolean;
}

export class ZiroGroupSettingsLocationConfig extends ZiroFieldComponentConfig {
  constructor(public buttons: ZiroRadioButtonSelect[]) {
    super();
  }
}

export interface GroupSettingsLocationFieldEntity {
  siteIds: number[];
  clusterId: number;
}

export enum GroupSettingsLocationOptionType {
  CLUSTER = 'CLUSTER',
  SITES = 'SITES',
}

export interface GroupSettingsLocationFieldData {
  option: GroupSettingsLocationOptionType;
  siteIds: ZiroSelectOption[];
  clusterId: ZiroSelectOption;
}

@Component({
  selector: 'ziro-group-settings-location',
  templateUrl: './dial-plan-management-group-settings-location.component.html',
  providers: [
    { provide: ZiroFieldAbstractDirective, useExisting: CiscoDialPlanManagementGroupSettingsLocationComponent },
  ],
  standalone: false,
})
export class CiscoDialPlanManagementGroupSettingsLocationComponent extends ZiroFieldAbstractDirective<
  GroupSettingsLocationFieldEntity,
  GroupSettingsLocationFieldData,
  ZiroGroupSettingsLocationConfig
> {
  private uniqueIdService = inject(UniqueIdService);

  radioSelect = [] as ZiroRadioButtonSelect[];
  uniqueRadioId: string;
  isLoading: boolean;
  initialValues: ZiroSelectValue[];
  bindValue: string;
  searchedTerm = '';

  readonly searchHasChanged = output<string>();
  readonly selectWasCleared = output<void>();
  readonly selectBlur = output<void>();

  get selectedValue(): ZiroSelectOption | ZiroSelectOption[] {
    return this.fieldData.option === GroupSettingsLocationOptionType.SITES
      ? this.fieldData.siteIds
      : this.fieldData.clusterId;
  }

  set selectedValue(value: any) {
    if (this.fieldData.option === GroupSettingsLocationOptionType.SITES) {
      this.fieldData.siteIds = value;
      this.fieldData.clusterId = null;
    } else {
      this.fieldData.clusterId = value;
      this.fieldData.siteIds = [];
    }
  }

  constructor() {
    super();
    const uniqueIdService = this.uniqueIdService;
    this.uniqueRadioId = uniqueIdService.getUniqueId();
  }

  applyComponentConfig: (config: ZiroGroupSettingsLocationConfig) => void = (
    config: ZiroGroupSettingsLocationConfig,
  ) => {
    this.radioSelect = config.buttons;
  };

  toFieldData = (entity: GroupSettingsLocationFieldEntity): GroupSettingsLocationFieldData => {
    const clusterObj = this.radioSelect.find((b) => b.value === GroupSettingsLocationOptionType.CLUSTER);
    const clusterLabel = entity.clusterId
      ? clusterObj.options.find((o: ZiroSelectOption) => o.value === entity.clusterId).label
      : '';

    const siteObj = this.radioSelect.find((b) => b.value === GroupSettingsLocationOptionType.SITES);
    const siteLabels = entity.siteIds.map((id: number) => {
      const optionMatch = siteObj.options.find((o: ZiroSelectOption) => o.value === id);
      return {
        label: optionMatch.label,
        value: id,
        group: optionMatch.group,
      };
    });

    return {
      option: entity.clusterId ? GroupSettingsLocationOptionType.CLUSTER : GroupSettingsLocationOptionType.SITES,
      siteIds: [...siteLabels],
      clusterId: entity.clusterId
        ? {
            value: entity.clusterId,
            label: clusterLabel,
          }
        : null,
    };
  };

  toEntity = (fieldData: GroupSettingsLocationFieldData): GroupSettingsLocationFieldEntity => {
    return {
      siteIds: fieldData.siteIds.map((opt: ZiroSelectOption) => opt.value),
      clusterId: fieldData.clusterId ? fieldData.clusterId.value : null,
    };
  };

  onRadioChange($event: GroupSettingsLocationOptionType, ngModelDir: NgModel): void {
    this.updateStateAndSelf(
      {
        option: $event,
        clusterId: null,
        siteIds: [],
      },
      ngModelDir,
    );
  }

  onSelectChange($event: any, ngModelDir: NgModel): void {
    this.updateStateAndSelf(
      {
        option: isArray($event) ? GroupSettingsLocationOptionType.SITES : GroupSettingsLocationOptionType.CLUSTER,
        clusterId: isArray($event) ? null : $event,
        siteIds: isArray($event) ? $event : [],
      },
      ngModelDir,
    );
    this.selectedValue = isArray($event) ? [...$event] : $event;
  }

  addInitialEntityToOptions<SV extends ZiroSelectValue | string>(
    options: string[] | ZiroSelectValue[],
    entity: any,
  ): void {
    // we have to use a generic type because typescript doesn't like it when you push elements to a union-typed array.
    let addToInitialValues = false;
    if (!this.initialValues) {
      this.initialValues = [];
      addToInitialValues = true;
    }

    if (!entity) {
      return;
    }

    // Multi Select?
    if (Array.isArray(entity)) {
      (entity as SV[]).forEach((value) => {
        if (
          (this.bindValue &&
            !options.some((o) => isEqual(ZiroSelectService.lookupNestedValue(o, this.bindValue), value))) ||
          (!this.bindValue && !options.some((o) => isEqual(o, value)))
        ) {
          (options as SV[]).unshift(value);
          if (addToInitialValues) {
            this.initialValues.push(value);
          }
        }
      });
    } else {
      if (
        (this.bindValue &&
          !options.some((o) => isEqual(ZiroSelectService.lookupNestedValue(o, this.bindValue), entity))) ||
        (!this.bindValue && !options.some((o) => isEqual(o, entity)))
      ) {
        (options as SV[]).unshift(entity as SV);
        if (addToInitialValues) {
          this.initialValues.push(entity);
        }
      }
    }
  }

  triggerLoading: () => boolean = () => false;

  handleSearchInput($event: { term: string; items: any[] }): void {
    this.searchedTerm = $event.term;
    this.searchHasChanged.emit(this.searchedTerm);
  }

  handleBlur(): void {
    this.selectBlur.emit();
  }
}
